// Generic app wording. Trip-specific copy (names, places, cards, sound effects) lives in the pack.
import { APP_NAME, STUDIO_NAME } from '../../shared/brand'
import { MACHINE_WORDS, type MachineWords } from '../../shared/machineWords'
import { PARENT_MODE_MINUTES } from '../domain/parentMode'

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
/** "1 plan", "0 plans". */
const count = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`

const ymd = (date: string) => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return { y, m, d, wd: new Date(Date.UTC(y, m - 1, d)).getUTCDay() }
}

export const fmt = {
  /** "WED 13 JAN" */
  dayCaps: (date: string) => {
    const { m, d, wd } = ymd(date)
    return `${DAYS[wd]} ${d} ${MONTHS[m - 1]}`
  },
  /** "Wed 13 Jan" */
  day: (date: string) => {
    const { m, d, wd } = ymd(date)
    return `${DAYS_SHORT[wd]} ${d} ${MONTHS_SHORT[m - 1]}`
  },
  /** "Tue 5 – Thu 7 Jan" (month once when shared) */
  dayRange: (a: string, b: string) => {
    const A = ymd(a)
    const B = ymd(b)
    const left = A.m === B.m ? `${DAYS_SHORT[A.wd]} ${A.d}` : fmt.day(a)
    return `${left} – ${fmt.day(b)}`
  },
  /** "5–30 Jan 2027" */
  tripDates: (a: string, b: string) => {
    const A = ymd(a)
    const B = ymd(b)
    return A.m === B.m ? `${A.d}–${B.d} ${MONTHS_SHORT[B.m - 1]} ${B.y}` : `${A.d} ${MONTHS_SHORT[A.m - 1]} – ${B.d} ${MONTHS_SHORT[B.m - 1]} ${B.y}`
  },
  /** "5–7 Jan", "30 Jan – 2 Feb", "5 Jan" (the Map's stop bar) */
  shortRange: (a: string, b: string) => {
    const A = ymd(a)
    const B = ymd(b)
    if (a === b) return `${B.d} ${MONTHS_SHORT[B.m - 1]}`
    return A.m === B.m && A.y === B.y ? `${A.d}–${B.d} ${MONTHS_SHORT[B.m - 1]}` : `${A.d} ${MONTHS_SHORT[A.m - 1]} – ${B.d} ${MONTHS_SHORT[B.m - 1]}`
  },
  month: (date: string) => MONTHS_SHORT[ymd(date).m - 1] ?? '',
  /** "WED" */
  weekdayCaps: (date: string) => DAYS[ymd(date).wd] ?? '',
  /** "Wednesday" */
  weekday: (date: string) => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][ymd(date).wd] ?? '',
  temp: (c: number) => `${c < 0 ? '−' : ''}${Math.abs(Math.round(c))}°`,
  /** "12 min", "2 h 35 min", "now" */
  until: (minutes: number) => {
    if (minutes <= 0) return 'now'
    if (minutes < 60) return `${minutes} min`
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return m ? `${h} h ${m} min` : `${h} h`
  },
  days: (n: number) => (n === 1 ? '1 day' : `${n} days`),
  hhmm: (ms: number, tz: string) =>
    new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(ms),
  plural: (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`,
  cap: (s: string) => s.charAt(0).toUpperCase() + s.slice(1),
  /** "Niseko, Sapporo and Otaru". */
  list: (xs: string[]) => (xs.length < 2 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`),
}

/**
 * Row and button labels that help text points to (WI-22). The gate, Install and Privacy pages
 * build their "where to find it" paths from these, so they always name what the screens say.
 */
const LABEL = {
  settings: 'Settings',
  family: 'Family & phones',
  phones: 'Phones',
  link: 'Link a new phone',
  data: 'Our data',
  export: 'Download everything',
} as const
/** A path through the app, as the screens label it: "Settings › Family & phones › Phones". */
const path = (...labels: string[]) => labels.join(' › ')
/** Where a grown-up makes a code for a new phone. */
export const LINK_PATH = path(LABEL.settings, LABEL.family, LABEL.phones, LABEL.link)
/** Where the parent downloads everything the server holds. */
export const EXPORT_PATH = path(LABEL.settings, LABEL.family, LABEL.data, LABEL.export)
/** E7: the public page that deletes a family with its recovery code (worker/pages.ts). */
export const DELETE_ACCOUNT_PATH = '/delete-account'

export const t = {
  /**
   * Each reward machine's own words (B16). Child-facing strings take `w` (the trip's machine,
   * useMachineWords) instead of saying "capsule".
   */
  machines: MACHINE_WORDS,

  tabs: { map: 'Map', trip: 'Trip', learn: 'Learn', arcade: 'Arcade', crate: 'Crate', organiser: 'Organiser' },
  /** What a dock badge means, read with its tab (the Map's action card's cues, owner 23 Oct). */
  dockBadge: {
    plays: (n: number) => `${fmt.plural(n, 'play')} waiting`,
    learnLeft: (n: number) => `${fmt.plural(n, 'right answer')} to go today`,
    today: 'A plan today',
    crateNew: (n: number) => `${n} new since you last looked`,
    needsYou: (n: number) => `${n} waiting for you`,
  },
  backAgain: 'Press back again to close',

  who: {
    title: "Who's on\nthis phone?",
    sub: 'Pick once. You can switch in Settings.',
    player: 'PLAYER',
    organiser: 'ORGANISER',
    playerLine: (w: MachineWords) => `${fmt.cap(w.many)}, crate, the map`,
    organiserLine: 'Plans days, hands out bonus plays',
    needsSignal: 'Needs signal once. Works offline after that.',
    offline: 'No signal. Connect once to pick who you are.',
    parentLine: 'Parent · PIN',
    gone: 'That person has changed. Pick again.',
    // Parent mode (WI-17): a grown-up picked on a child's phone.
    parentMode: (grownUp: string, child: string) => `${grownUp} — parent mode for ${PARENT_MODE_MINUTES} min on ${child}'s phone`,
    parentModeLine: (child: string) => `Parent mode for ${PARENT_MODE_MINUTES} min on ${child}'s phone`,
    setPinFirst: 'Set a PIN on your own phone first: a grown-up needs one to sign in on a child’s phone.',
    /** Settings › Switch's sheet title (WI-17). */
    switchTitle: 'Switch to',
  },

  /** Parent mode's pill (WI-17): the Map's trip pill while a grown-up is on a child's phone. */
  parentMode: {
    left: (min: number) => `Parent mode · ${min} min`,
    /** Read out in the last minute, when the pill turns yellow. */
    warn: (child: string, min: number) => `Parent mode · ${min} min · back to ${child} soon`,
    done: 'Done',
  },

  /** An admin with no PIN (a family from before PINs were mandatory) sets one before going on (WI-16). */
  pinGate: {
    title: 'Choose your PIN',
    sub: 'Six digits. Every parent needs one now: it’s asked when picking you on a phone, and before big changes like deleting.',
  },

  join: {
    title: 'Join your family',
    sub: `Ask a grown-up to open ${LINK_PATH} on their phone.`,
    scan: 'Or point your camera at the QR code on their phone.',
    type: 'Type the 6 letters',
    placeholder: 'ABC123',
    go: 'Join',
    hint: 'Join turns on when all 6 letters are in. A code works once, for 7 days.',
    badCode: "That code didn't work. Codes work once, for 7 days.",
  },

  /** A phone in no family yet (WI-21, WI-37; mockups docs/mockups/family-start.html). */
  // E7: the public page that deletes a family (worker/pages.ts, plain HTML).
  deleteAccount: {
    title: `Delete your ${APP_NAME} family`,
    inApp: 'In the app: Organiser › Family › Delete the family’s data. It needs the parent’s PIN.',
    noPhoneTitle: 'Lost every phone?',
    noPhone: 'Type the family’s recovery code (you saved it when you set the family up) and the word DELETE. Everything of the family is deleted from our server: its people, trips, progress, phones and purchases. This can’t be undone.',
    codeLabel: 'Recovery code',
    confirmLabel: 'Type DELETE',
    button: 'Delete the family',
    typeDelete: 'Type the word DELETE to confirm.',
    tooMany: 'Too many tries. Wait a minute and try again.',
    badCode: 'That recovery code doesn’t match a family.',
    noCodeTitle: 'No recovery code?',
    noCode: (contact: string) => `Email ${contact} from any address. We'll ask a few questions about the family to be sure it's yours, then delete it.`,
    noCodeNoContact: 'Reinstall the app and use “Lost every phone?” to make a new code, or wait: a family with no phone seen for 18 months is deleted automatically.',
    doneTitle: 'Deleted',
    done: 'The family and everything in it are gone from our server. Phones still signed in will land on “Set it up” next time.',
  },
  // C6, C7: country packs (mockups docs/mockups/packs.html).
  packs: {
    stillWrapped: (island: string) => `${island} is still wrapped`,
    childSub: (pack: string) => `It's one of the ${pack} islands. Ask your grown-up to unwrap them all.`,
    stillFree: (island: string, deck: string) => `You can still play on ${island}, and learn ${deck}.`,
    grownUpSub: (pack: string) => `It's one of the ${pack} islands. Unwrap ${pack} to play them all.`,
    ask: 'Ask my grown-up',
    asked: 'Asked ✓',
    askedToast: 'Your grown-up will see it',
    unwrap: 'Unwrap',
    unwrapPack: (pack: string) => `Unwrap ${pack}`,
    unwrapTitle: (pack: string) => `Unwrap ${pack}`,
    unwrapFor: (price: string) => `Unwrap for ${price}`,
    once: 'Once, for the whole family, on every phone.',
    getsIslands: 'All its islands to explore',
    getsWords: 'Every deck of its language: letters, numbers and everyday words',
    getsSounds: 'Every sound and voice, offline on the trip',
    pinTitle: 'Your PIN first',
    restore: 'Already bought? Restore',
    nothingToRestore: 'Nothing to restore on this phone.',
    unwrapped: (pack: string) => `${pack} is unwrapped! 🎀`,
    yours: (pack: string) => `${pack} is your family's.`,
    askParent: 'Ask the parent to unwrap it.',
    notYet: "Buying isn't open yet.",
    failed: "That didn't go through. Nothing was charged; try again.",
    needsYou: (child: string, pack: string) => `${child} asked to unwrap ${pack}`,
    needsYouSub: 'Every island, word and sound of it',
    destinations: 'Destinations',
    atlasTitle: 'Where to next?',
    atlasSub: 'Each country is a pack: its islands, its language and its sounds.',
    yoursPill: 'Yours',
    comingSoon: 'Coming soon',
    tellMe: 'Tell me',
    told: "We'll tell you ✓",
    elsewhere: 'Somewhere else',
    vote: 'Vote for it',
    voted: 'Voted ✓',
  },
  // A2: the old workers.dev address, once the app lives at its own (mockups g03, g04).
  moved: {
    title: `${APP_NAME} has moved`,
    grownUp: 'Everything your family did is kept. Open the new address on this phone and get back in with your recovery code.',
    child: 'Your islands, toys and words are all kept. Ask your grown-up for a link code, then join again at the new address.',
    open: 'Open the new address',
    recovery: 'Make a recovery code first',
    then: 'Then make link codes there for the other phones.',
  },
  welcome: {
    title: APP_NAME,
    sub: 'A trip game for the whole family: islands to explore, words to learn, treats to win.',
    setUp: 'Set it up',
    setUpSub: 'For my family, or just me',
    join: 'Join a family',
    joinSub: 'I have a code or a link',
    lost: 'Lost every phone? Use your recovery code',
    install: 'How to install',
    privacy: 'What we keep',
    back: 'Back',
    next: 'Next',
    // Who's travelling?
    // E10: the neutral age screen (never says where the line is).
    ageTitle: 'Before we start',
    ageSub: 'What year were you born?',
    ageEntered: (y: string) => (y ? `Year: ${y.split('').join(' ')}` : 'No year yet'),
    ageWrong: 'That doesn’t look like a year. Try again.',
    youngTitle: 'Ask a grown-up',
    youngSub: `A grown-up sets up ${APP_NAME} for the family. Then they can send you a link code to join on this phone.`,
    whoTitle: "Who's travelling?",
    whoSub: "You can add people later, so this isn't for ever.",
    solo: 'Just me',
    soloSub: 'I explore, learn and win treats myself',
    family: 'Me and my family',
    familySub: 'I set up the trip; the children play',
    // Steps
    stepSolo: 'Just me',
    stepFamily: 'My family',
    youTitle: 'About you',
    youSub: 'A first name or nickname. No email, no account.',
    yourName: 'Your name',
    familyTitle: 'You and your family',
    familySubline: 'Only first names or nicknames. No email, no accounts.',
    familyName: 'Family name',
    familyNamePh: 'e.g. The Martins',
    callYou: 'What the children call you',
    callYouPh: 'e.g. Mum, Dad, Grandpa',
    coat: 'Your coat',
    coatName: (n: number) => `Coat colour ${n}`,
    pinTitle: 'Choose your PIN',
    pinSub: "Six digits. Needed to pick you on any phone, and before big changes like deleting your data. Children don't get one.",
    pinAgain: 'Type it once more',
    pinAgainSub: 'To check it.',
    pinMismatch: "Those didn't match. Choose your PIN again.",
    kidsTitle: "Who's coming?",
    kidsSub: 'Up to four children. Their age sets sensible rules; you can change them later.',
    kidName: 'Nickname',
    addKid: '+ Add a child',
    removeKid: (name: string) => `Remove ${name || 'this child'}`,
    kidsLater: 'Add children later',
    ages: { u8: 'Under 8', '8-12': '8–12', '13+': '13+' } as Record<'u8' | '8-12' | '13+', string>,
    codeTitle: 'Keep this code safe',
    codeSub: "If every phone is lost, this code is the only way back to your trips, Crate and progress. We can't reset it for you: there's no email.",
    copy: 'Copy',
    copied: 'Copied',
    saved: "I've written it down or saved it",
    done: 'Done: on to the trip',
    recoverLabel: 'Recovery',
    recoverTitle: 'Use your recovery code',
    recoverSub: `The 16 letters and numbers you saved when you set ${APP_NAME} up. You can then choose a new PIN.`,
    recoverGo: 'Get back in',
    recoverBad: "That code didn't work. Check it, or ask whoever set the family up.",
    failed: "Couldn't set it up. Check your signal and try again.",
  },

  pin: {
    hi: (name: string) => `Hi ${name}`,
    enter: 'Enter your PIN',
    back: 'Back',
    delete: 'Delete',
    entered: (n: number) => `${n} of 6 digits`,
    wrong: (left: number) => (left === 1 ? 'Wrong PIN · 1 try left' : `Wrong PIN · ${left} tries left`),
    locked: (time: string) => `Too many tries. Try again at ${time}.`,
    setTitle: 'Choose a parent PIN',
    setSub: 'Six digits. Needed to pick you on a phone and for big changes.',
    confirmTitle: 'Once more',
    confirmSub: 'Type the same six digits again.',
    mismatch: "Those didn't match. Start again.",
    oldTitle: 'Current PIN',
    oldSub: 'Enter your current PIN first.',
    saved: 'PIN saved',
    // B3: the grown-up screens after 10 minutes away, on a grown-up's own phone.
    lockTitle: 'Grown-ups only',
    lockSub: 'Enter your PIN to carry on.',
  },

  climate: (date: string, c: number) => `${fmt.month(date)} ≈ ${fmt.temp(c)}`,
  liveTemp: (c: number) => `${fmt.temp(c)} now`,

  map: {
    /** The top pill's one fact (WI-04): where the trip stands, counting only to the next stop. */
    pill: {
      pre: (days: number, stop: string) => `${fmt.days(days)} to ${stop}`,
      preSurprise: (days: number) => `${fmt.days(days)} to a surprise`,
      during: (day: number, stop: string) => `Day ${day} · ${stop}`,
      after: 'Trip done',
      back: 'back ↩',
      backLabel: (text: string) => `${text}. Back to now`,
    },
    /** The card under it: the one thing to do now. */
    /** The stop bar above the dock: the journey's stops, repeats included. */
    stops: {
      label: 'Trip stops',
      prev: 'Previous stop',
      next: 'Next stop',
      surprise: 'Surprise',
      dot: (n: number, name: string, dates: string | null) => `Stop ${n}: ${name}${dates ? ` · ${dates}` : ''}`,
    },
    flatTitle: 'The trip · flat map',
    resting: 'The 3D map is resting to save battery.\nArcade, Trip and Crate all work offline.',
    soon: 'The island arrives in the next update.',
    /** `things`: what to tap on the island showing (placeHints), or the landmarks. */
    hint: (things?: string) => `Drag to spin · pinch to zoom · tap ${things ?? 'the landmarks'}, anything`,
    /** Each place's two most fun things to tap (each starts its show or says its word), for the hint. */
    placeHints: {
      kyoto: 'the temple bell, the koi',
      tokyo: 'the scramble crossing, the sumo ring',
      osaka: 'the takoyaki stall, the giant crab',
      nara: 'the deer, the mochi pounders',
      castletown: 'the train, the castle',
      shirakawago: 'the bus, the clockwork float',
      miyajima: 'the great torii, the deer',
      fuji: 'the ropeway, the black eggs',
      kamakura: 'the Great Buddha, the surfers',
      bigcity: 'the station, the yakitori alley',
      fukuoka: 'the yatai stalls, the tower',
      okinawa: 'the shisa, the sea turtle',
      village: 'the scarecrows, the little truck',
      sapporo: 'the streetcar, the clock tower',
      otaru: 'the boat, the steam clock',
      niseko: 'the skiers, the bus',
      seoul: 'the palace gate, the Haechi',
      hangang: 'the bridge fountain, the cable car',
    } as Readonly<Record<string, string>>,
    canvas: (titles: string[]) => (titles.length ? `3D map of the trip: floating islands for ${fmt.list(titles)}` : '3D map of the trip'),
    fallback: 'The 3D map couldn’t start on this phone, so here’s the flat one.',
    // §8.3: the ▾ is drawn beside it, not read out ("World", expanded or not).
    worldButton: 'World',
    /** The Map's heading, for screen readers only (WI-28): the place showing. */
    heading: (place: string) => (place ? `Map · ${place}` : 'Map'),
    /** The chip over the player's own figure on the island (WI-05). */
    you: 'You',
    /**
     * The NOW / NEXT pin: a marker with a bed on it and no words (the top pill and the stop bar
     * already say where), named for screen readers. It marks where the family stays, not a
     * landmark they'll visit.
     */
    stay: (stage: string) => `Where you stay: ${stage}`,
    /** Any other stop's pin: a plain marker without words (a dot, or a tick once visited), named here. */
    stopLater: (stage: string) => `${stage} (a later stop)`,
    stopDone: (stage: string) => `${stage} (visited)`,
    /** The sound-word toast (WI-06): a word from the island, tap for its mini card. */
    sfx: {
      line: (reading: string, meaning: string) => (meaning ? `${reading} · ${meaning}` : reading),
      means: (meaning: string) => `means “${meaning}”`,
      hear: '🔊 Hear it',
      close: 'Close',
    },
    islands: 'Islands',
    islandsMore: 'More islands',
    islandsBack: 'Earlier islands',
    world: {
      title: 'World',
      time: 'Time',
      weather: 'Weather',
      live: 'Live',
      surprise: 'Surprise',
      spotted: 'Spotted',
      notYet: 'Something rare…',
      onIsland: 'On the island',
      note: 'Live follows the trip clock and the weather. Purple surprises are rare: keep an eye out.',
      season: 'Season',
      sounds: 'Sounds',
      mixed: 'Mixed',
      synthOnly: 'Synth',
      voice: 'Voice',
      off: 'Off',
      on: 'On',
      explore: 'Explore all',
      exploreNote: 'Explore all shows every island and place in the switcher, not just this trip’s.',
      done: 'Done',
    },
    // A child's World sheet (WI-07): pictures for the time and the weather, the stickers they've
    // spotted, and one Surprise me (a common act: a surprise never counts as spotting a rare one).
    look: {
      title: 'Look',
      surpriseMe: '✨ Surprise me',
      shelf: 'My spotted stickers',
      shelfCount: (n: number, of: number) => `${n} of ${of}`,
      hidden: 'Not spotted yet',
      tod: { now: 'Now', day: 'Day', dusk: 'Dusk', night: 'Night' } as Record<string, string>,
      wx: { now: 'Now', clear: 'Clear', snow: 'Snow', blizzard: 'Blizzard' } as Record<string, string>,
    },
  },

  trip: {
    title: 'Trip',
    // A child who isn't coming (B9): the trip is the family's, theirs to follow.
    familyTripTag: 'FAMILY TRIP',
    familyTrip: "You're not on this one. Follow along with the family here; your own world stays just as it is.",
    toggle: 'Today · All days',
    /** A journey's chip, read out (WI-41): pressing it toots the first vehicle. */
    playJourney: (legs: string) => `Journey: ${legs}. Press to toot.`,
    today: 'Today',
    allDays: 'All days',
    firstDay: 'First day',
    daysPre: 'Days',
    days: 'Days of the trip',
    dayOf: (n: number, total: number) => `Day ${n} of ${total}`,
    booked: 'BOOKED',
    want: 'WANT TO GO',
    surprise: '🎁 SURPRISE',
    nextUp: 'NEXT UP',
    anyTime: 'Any time',
    empty: 'Nothing planned for this day yet.',
    emptyMystery: 'Somewhere new. Plans appear when it opens.',
    imHere: "📍 I'm here",
    checking: 'Checking…',
    found: '✓ FOUND',
    checkedIn: (name: string, plays: number) => `Checked in at ${name}! A new toy for your Crate${plays ? ` · +${fmt.plural(plays, 'play')}` : ''}`,
    notNear: (m: number) => `Not there yet: about ${m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`} away`,
    checkinDenied: 'The phone said no to location. A grown-up can allow it in the browser settings.',
    checkinNoFix: "Couldn't find where the phone is. Try again outside.",
    checkinPrivacy: "Checking in looks at where the phone is once, on this phone. It isn't saved or sent.",
    summary: (name: string, days: number) => `${name} · ${fmt.days(days)}`,
    startsIn: (n: number) => `Starts in ${fmt.days(n)}`,
    stage: (i: number, name: string) => `${i} · ${name}`,
    // §8.3: a journey chip as a button, and the act it (or a tap, or the director) plays.
    journeyAct: (act: string) => `On the journey: ${act}`,
    stageDays: (a: string, b: string, n: number) => `${fmt.dayRange(a, b)} · ${fmt.days(n)}`,
    /** The same, as the parts that each stay on one line (WI-40 #7: screens/When). */
    stageWhen: (a: string, b: string, n: number) => [fmt.dayRange(a, b), fmt.days(n)],
    next: 'NEXT',
    now: 'NOW',
    done: 'DONE',
    mystery: (days: number, opensIn: number) => `${fmt.days(days)} somewhere new · ${opensIn ? `opens in ${fmt.days(opensIn)}` : 'opens today'}`,
    mysteryToday: (days: number, minutes: number) => `${fmt.days(days)} somewhere new · opens in ${fmt.until(minutes)}!`,
    clue: (clue: string) => `Clue: “${clue}”`,
  },

  arcade: {
    title: 'Arcade',
    soonPill: 'Opening soon',
    beingBuilt: 'BEING BUILT',
    soonButton: 'Kana Capsules arrive next update',
    katakana: 'Katakana',
    playsToday: (n: number) => `${fmt.plural(n, 'play')} today`,
    machineLabel: (title: string, w: MachineWords, locked: boolean) => `${title} ${w.cabinet}${locked ? ', locked' : ''}. ${w === t.machines.gashapon ? 'Drag the dial round or tap Twist.' : `Tap ${fmt.cap(w.verb)}.`}`,
    level: (n: number) => `Lv ${n}`,
    /** One line under the machine: what a play of this card set asks (WI-11, L2-08). */
    blurbs: {
      'kana-capsules': 'Read characters: pick the sound each one makes',
      'speak-up': 'Hear a phrase, pick what it means, then say it',
      'number-rush': 'Read and hear numbers, prices and times',
      explorer: 'Questions about the places on your trip',
    } as Record<string, string>,
    soon: 'SOON',
    signHunt: 'Sign Hunt',
    camera: 'camera',
    bonusNote: (who: string, n: number) => `Bonus from ${who}! +${fmt.plural(n, 'play')}`,
    bonusButton: 'Bonus plays',
    noPlays: 'No plays left today',
    earnMore: 'Finish your Learn goal or check in to earn more',
    myPrizes: (n: number) => `🎟\u{FE0F} ${n}`,
    myPrizesLabel: (n: number) => `My prizes, ${n} waiting`,
    playsLabel: (n: number) => `${fmt.plural(n, 'play')} today: how you earned them`,
    // A grown-up who doesn't play (WI-37) has no plays of their own.
    notPlaying: "You're not playing",
    notPlayingAdmin: 'Want plays, prizes and a Learn goal too? Turn them on in Family & phones › you.',
    notPlayingAdult: 'The parent can turn on plays for you in Family & phones.',
    // WI-13: a twist is the reward.
    whatsInside: (w: MachineWords) => `Every ${w.one} holds a prize: something for your shelf, a new act for your island, or a real treat`,
    shelf: (n: number) => (n ? `My shelf · ${n} collected` : 'My shelf'),
    actsOpen: (n: number) => `${fmt.plural(n, 'act')} open on your islands`,
    tasks: (n: number) => `${fmt.plural(n, 'holiday task')} to do`,
    tasksSub: 'Each one earns more plays',
  },

  plays: {
    title: "Today's plays",
    howEarned: 'How you earned them',
    most: (n: number) => `most a day: ${n}`,
    free: 'Free play',
    freeSub: (n: number) => `Every day · +${n}`,
    goal: (done: number, goal: number) => `Learn: ${Math.min(done, goal)} of ${goal} right answers`,
    goalDoneSub: (n: number) => `Daily goal done · +${n}`,
    goalTodoSub: (n: number) => `Reach today's goal for +${n}`,
    checkin: (place: string) => `Checked in: ${place}`,
    checkinSub: (n: number) => `A planned place · +${n}`,
    activitySub: (n: number) => `Done! · +${n}`,
    activityAsk: (who: string, n: number) => `Waiting for ${who} to say it's done · +${n}`,
    bonus: (who: string) => `Bonus from ${who}`,
    bonusSub: (n: number) => `+${n}`,
    carried: (n: number) => `${fmt.plural(n, 'play')} saved from before`,
    waiting: (n: number) => `${fmt.plural(n, 'more play', 'more plays')} wait for tomorrow (over today's most)`,
    toMachine: 'Back to the machine',
    goalDone: (n: number) => `Daily goal done! +${fmt.plural(n, 'play')}`,
    ask: 'ASK',
    todo: 'TO DO',
    // Holiday tasks (WI-13b).
    tasks: 'Holiday tasks',
    taskDoneSub: (n: number) => `Holiday task done · +${n}`,
    taskSelf: (n: number) => `Tick it when it's done · +${n}`,
    taskAsk: (who: string, n: number) => `${who} says when it's done · +${n}`,
    taskAsked: (who: string) => `Waiting for ${who} to say it's done`,
    tickDone: 'Done!',
    tickAsk: 'Ask',
    grownUp: 'a grown-up',
  },

  myPrizes: {
    title: 'My prizes',
    waiting: 'Waiting for a grown-up',
    cashed: 'Cashed in',
    machine: 'Still in the machine',
    forYou: 'for you',
    wonSub: (day: string, who: string) => `Won ${day} · ${who} has been told`,
    doneSub: (day: string, note?: string) => [day, note].filter(Boolean).join(' · '),
    tagWaiting: 'WAITING',
    tagDone: 'DONE',
    stamp: 'CASHED IN',
    none: (w: MachineWords) => `Nothing yet. ${fmt.cap(w.verb)} a ${w.one}!`,
    note: 'Nothing expires. Greyed-out prizes are paused for now.',
    // WI-14: a tap on a prize in the machine shows the parent's note.
    noNote: (title: string) => `${title}: a real prize. Win it in the machine.`,
  },

  prizeReveal: {
    rarity: { common: 'PRIZE', rare: 'RARE PRIZE', legendary: 'LEGENDARY PRIZE' } as Record<string, string>,
    real: 'A real one. Ask a grown-up to cash it in.',
    /** A grown-up player's real prize is theirs at once (WI-37). */
    realSelf: 'A real one. Treat yourself!',
    instant: "It's yours!",
    noteTitle: (who: string) => `${who} gets a note`,
    noteSub: "It waits in your prizes until it's cashed in. It never runs out.",
    into: 'Into my prizes',
  },

  bonus: {
    who: 'Who gets them',
    title: 'Bonus plays',
    // The same progress model as Learn (WI-10, L4-15): cards met and learned, not "0 characters".
    summary: (name: string, level: number, met: number, learned: number, streak: number) =>
      `${name}: Lv ${level} · ${met} met · ${learned} learned${streak > 1 ? ` · ${streak} days in a row` : ''}.`,
    left: (name: string, n: number) => `${name} has ${fmt.plural(n, 'play')} left today`,
    plays: 'Bonus plays',
    more: 'More plays',
    fewer: 'Fewer plays',
    give: (n: number) => (n ? `Give ${fmt.plural(n, 'play')}` : 'Choose how many to give'),
    note: 'A bonus goes over the daily most. They’ll see the bonus next time they open the Arcade.',
  },

  session: {
    close: 'Close',
    hearIt: 'Hear it',
    /** The round speaker under a card: replay, plainly not an answer. */
    hearAgain: 'Hear it again',
    // WI-26: no recording, and no voice for the language installed on this phone.
    noVoice: 'No voice on this phone',
    noVoiceSub: 'Read it instead. A grown-up can add the voice in the phone’s text-to-speech settings.',
    skip: 'Skip',
    /** After a miss or a skip, once the answer is shown (never "Got it": that read as self-grading). */
    next: 'Next',
    /** A new listening card's first time: asked straight away, its sound heard first (no separate meet card). */
    newHint: 'New: listen first',
    /** …a new card you read (kana, a word, a price): its sound comes after the answer (K2). */
    newHintRead: 'New',
    /** The new-sounds warm-up's question (WI-09): the sound plays, the options are characters. */
    whichChar: 'Which character makes this sound?',
    /** A phrase card's question (Everyday words / Speak Up): "onegaishimasu: what does it mean?" */
    whatMeans: (romaji: string) => (romaji ? `${romaji}: what does it mean?` : 'What does it mean?'),
    /** …its reveal line, "onegaishimasu: please (polite)". */
    means: (romaji: string, meaning: string) => (romaji ? `${romaji}: ${meaning}` : meaning),
    /** …and the moment to say it out loud after a right answer (not graded). */
    sayIt: (romaji: string) => `Now say it out loud: ${romaji}`,
    xp: (n: number) => `+${n} XP`,
    combo: (n: number) => `x${n}`,
    tapToHear: 'Tap to hear it again',
  },

  info: {
    back: 'Back',
    installTitle: `Get ${APP_NAME}`,
    what: `${APP_NAME} turns a family holiday into a game for the children: a 3D world of the trip, a little of the local language, a reward machine, and a crate of the places they collect.`,
    whatSub: 'A grown-up plans the trip and decides what each child sees and earns. It works offline, has no ads, and keeps the family private.',
    howTitle: 'Install it on an Android phone',
    /** The Play Store listing's button (shown once distribution.ts has its link). */
    getOnPlay: 'Get it on Google Play',
    /** …and the page's own install steps under it. */
    orFromPage: 'Or install it from this page:',
    installNow: `Install ${APP_NAME}`,
    installed: 'It’s installed on this phone. Open it from the home screen.',
    /** Shown inside the installed app instead of the steps. */
    anotherPhone: (store: boolean) =>
      store ? 'On another phone: get it on Google Play, or open this page there in Chrome.' : 'On another phone: open this page there in Chrome.',
    /** Step 2 points at the Install button only when this browser offered one (canPrompt). */
    steps: (canPrompt: boolean) => [
      'Open this page in Chrome.',
      canPrompt ? 'Tap Install above, or Chrome’s menu ⋮ › Install app (or Add to Home screen).' : 'Open Chrome’s menu ⋮ › Install app (or Add to Home screen).',
      `Open ${APP_NAME} from the home-screen icon from now on.`,
      'Open every tab once on wifi; after that it all works offline.',
    ],
    /** Under the steps: the same app either way (the Play Store one is this web app, wrapped). */
    offline: (store: boolean) =>
      store
        ? 'Google Play and this page install the same app, with the same family and progress.'
        : `${APP_NAME} is coming to Google Play. Until then, install it from this page: it’s the same app.`,
    joinTitle: 'Join a family',
    joinSub: `A grown-up in the family makes a code: ${LINK_PATH}. Scan its QR code, open the link, or type the code here.`,
    joinCode: 'Family code',
    privacyLink: `What ${APP_NAME} keeps, and what it doesn’t`,
    installLink: `How to install ${APP_NAME}`,
    privacyTitle: 'Privacy',
    privacyIntro: `${APP_NAME} is for families, children included, so it keeps as little as it can. Here is all of it, in plain words.`,
    privacy: [
      { title: 'What we keep', lines: [
        'For each person: a nickname, an age band (under 8, 8–12, 13+) and a figure. A parent (a family can have two) has a 6-digit PIN, and another grown-up can add one; PINs are stored only as a salted hash.',
        'The family’s recovery code, stored only as a hash: it can’t be read back, only checked.',
        'The trip and its plans, who is coming on it, and what each child does in the app: answers, plays, stickers and prizes, the rare things they spot on the map, places they want to go, and their own plans.',
        'A check-in keeps only “near” or “not near” a planned place. Never where the phone is.',
        'Which phones are linked to the family, each labelled by its device (like “Pixel 7” or “Android phone”), whose phone it is, who last signed in on it, and when it was last seen.',
        'If you turn notifications on: the address your browser gives for them, and the phone’s time zone (for quiet hours).',
        'Answers older than a year are folded into a short summary of each card’s progress; the answers themselves go.',
      ] },
      { title: 'Children', lines: [
        'A grown-up sets up the family. Children never type anything about themselves: they tap their own figure.',
        'A grown-up decides what each child sees and earns, and a PIN guards everything a grown-up can do.',
        'The location check-in only runs when a child taps “I’m here”, with the app open, and only after the phone asks permission.',
        'Setting up asks the year of birth only to check that a grown-up is doing it. It isn’t kept or sent anywhere.',
        'Children never see a price, a store or a link out of the app.',
        'A parent or guardian sets the family up, and by doing so agrees to what this page describes for their children.',
      ] },
      // C3, D2: buying a country pack.
      { title: 'Buying a pack', lines: [
        'A parent buys a country pack once, for the whole family, behind their PIN.',
        'On the web, Stripe takes the payment on its own page; in the Play app, Google Play does. We never see card details.',
        'We keep only which pack the family has, which store sold it and the store’s reference for the purchase.',
      ] },
      { title: 'What we never keep', lines: [
        'No email, phone number, real name, birthday, photos or contacts.',
        'No location trail: the phone checks its position once, on the phone, when a child taps “I’m here”.',
        'No ads, no tracking, no third-party analytics.',
      ] },
      { title: 'Who else sees anything', lines: [
        'Nobody outside your family. Each family is private; children see only what their grown-up allows.',
        'Live weather asks MET Norway for a stage’s map pin, rounded, never the phone’s position.',
        'Pasting a map link follows only its redirect to find the place; nothing about you is sent.',
        // WI-42: "Paste your bookings" (only when switched on).
        `Pasting your bookings sends only the text you paste, and the trip’s dates, to Claude (made by Anthropic) to read hotels and journeys out of it. ${APP_NAME} doesn’t keep it (Anthropic handles it under its API terms), and nothing is added to the trip until a grown-up checks it and taps Add.`,
        'Finding a place by name sends only the typed name to GeoNames’ place search.',
        `Notifications carry no content: they pass through your browser’s push service (Google’s, on Android), and the phone fetches its note from ${APP_NAME} when one arrives.`,
        `Everything travels encrypted (HTTPS). ${APP_NAME}’s server and database run on Cloudflare, who store them for us and use them for nothing else.`,
      ] },
      { title: 'Your data, your choice', lines: [
        `The parent can download everything as a file: ${EXPORT_PATH}.`,
        'The parent can delete the family’s data at any time; it needs the parent’s PIN. It’s gone from the server; uninstalling the app clears the phone.',
        'Everything works offline and lives on your phones first; the server only syncs it between them.',
        `Lost every phone? The family can be deleted with its recovery code at ${DELETE_ACCOUNT_PATH}, from any browser.`,
        'A family with no phone seen for 18 months is deleted automatically.',
      ] },
      { title: 'Who we are', lines: [`${APP_NAME} is made by ${STUDIO_NAME}.`] },
    ],
    /** The privacy page's last line, once distribution.ts names a contact. */
    contact: (who: string) => `Questions, or want something deleted? Contact ${who}.`,
    settingsRow: 'Install and privacy',
    settingsRowSub: `How to install on another phone, and what ${APP_NAME} keeps`,
  },

  past: {
    title: 'Past trips',
    sub: 'Every trip keeps its own Crate',
    open: '🧳 Past trips',
    none: 'No other trips yet.',
    note: (name: string) => `What ${name} found, stuck and won on each trip. Switching trips is for a grown-up, in Trips.`,
    found: (n: number) => (n === 1 ? '1 place found' : `${n} places found`),
    noFinds: 'No check-ins on this trip.',
    stickers: (n: number) => (n === 1 ? '1 sticker' : `${n} stickers`),
    prizes: (n: number) => (n === 1 ? '1 prize won' : `${n} prizes won`),
  },

  // B10 Trip book (lane 4's mockup docs/renders/trip-book).
  book: {
    thisTrip: '📖 This trip’s book',
    open: '📖 Open the trip book',
    shelf: '← Back',
    ourBook: 'Our trip book',
    dates: (dates: string, islands: number) => `${dates} · ${fmt.plural(islands, 'island')}`,
    who: (kids: string[]) => `${kids.join(' · ')} · and their grown-ups`,
    places: 'places found',
    acts: (rare: number) => (rare ? `acts opened · ${rare} rare` : 'acts opened'),
    words: 'words learned',
    collectibles: (prizes: number) => (prizes ? `collectibles · ${fmt.plural(prizes, 'prize')}` : 'collectibles'),
    stageTag: (n: number, name: string) => `${n} · ${name}`,
    stageDates: (dates: string, days: number) => `${dates} · ${fmt.plural(days, 'day')}`,
    checkedIn: (n: number) => `CHECKED IN ×${n}`,
    found: 'Found:',
    nothingYet: 'nothing checked in here yet',
    act: 'New act:',
    rareAct: 'Rare act:',
    actBy: (act: string, who: string) => (who ? `${act} (${who}’s key)` : act),
    task: 'Holiday task:',
    collectiblesFrom: (name: string) => `Collectibles from ${name}`,
    whatATrip: 'What a trip!',
    whoFound: 'Who found what',
    personLine: (places: number, acts: number, words: number, collectibles: number, best?: string) =>
      [fmt.plural(places, 'place'), fmt.plural(acts, 'act'), fmt.plural(words, 'word'), fmt.plural(collectibles, 'collectible'), best ? `opened ${best}` : ''].filter(Boolean).join(' · '),
    best: 'Best moments (the rarest acts)',
    next: 'Plan the next trip',
    backToShelf: 'Back to the Crate',
    theEnd: 'The end',
    pages: 'Pages',
    prev: 'Previous page',
    next_: 'Next page',
  },

  lookup: {
    where: 'Where (a town or city)',
    /** Neutral: used when the trip's country isn't known. */
    placeholder: 'e.g. a town or city name',
    /** An example place for a trip in that country (ISO 3166-1), for the lookup's placeholder. */
    examples: {
      JP: 'e.g. Hakodate, Japan',
      KR: 'e.g. Busan, South Korea',
      TH: 'e.g. Chiang Mai, Thailand',
      AU: 'e.g. Cairns, Australia',
      NZ: 'e.g. Queenstown, New Zealand',
      GB: 'e.g. York, England',
      FR: 'e.g. Annecy, France',
      IT: 'e.g. Verona, Italy',
      ES: 'e.g. Seville, Spain',
      PT: 'e.g. Lagos, Portugal',
      US: 'e.g. Santa Fe, USA',
      CA: 'e.g. Banff, Canada',
    } as Record<string, string>,
    find: 'Find',
    results: 'Places found',
    none: 'Nothing found by that name. Try a nearby town, or set the pin by hand.',
    offline: 'Finding a place needs signal. You can set the pin by hand.',
  },

  notify: {
    title: 'Notifications on this phone',
    childSub: 'When a surprise opens, and when your free play is ready',
    grownUpSub: 'Needs you: prizes to cash in, activities, suggestions',
    soloSub: 'When your free play is ready',
    // Until the server can push (no VAPID keys yet).
    soon: 'Coming soon',
    soonSub: 'Notifications arrive in an update soon',
    unsupported: "This browser can't show notifications.",
    failed: {
      denied: 'Notifications are blocked for this app. Allow them in the phone’s settings.',
      unsupported: "This browser can't show notifications.",
      'no-key': 'Notifications aren’t set up on the server yet.',
    } as Record<string, string>,
    offline: 'Notifications need signal to change.',
    quiet: 'Quiet hours',
    quietSub: 'No notifications in between, on every phone',
    quietFrom: 'Quiet from',
    quietTo: 'Quiet until',
    quietSaved: (from: string, to: string) => `Quiet ${from}–${to}`,
    child: 'Notifications',
    childSwitchSub: 'Surprise days and the free play, never in quiet hours',
  },

  unwrap: {
    label: (name: string) => `Unwrapped: ${name}`,
    tag: 'UNWRAPPED!',
    sub: (days: string) => `${days} here. Have a look at the island.`,
    skip: 'Tap to carry on',
  },

  reward: {
    // WI-13: the twist opens straight onto one reward (mockups docs/renders/rewards3d rw01–rw03).
    newTag: 'NEW FOR YOUR SHELF',
    againTag: (n: number) => `YOU HAVE ${n}`,
    goldTag: 'GOLD! THREE OF A KIND',
    setLine: (set: string, have: number, of: number) => `${set} set · ${have} of ${of}`,
    goldHint: 'Three the same turn gold ✨',
    setDone: (set: string) => `${set} set complete! A bonus key is on its way.`,
    shelf: 'Put it on my shelf',
    keyTag: (island: string) => `ACT KEY · NEW ON ${island.toUpperCase()}`,
    keyTitle: (act: string) => `${act}!`,
    keySub: "It's on your island now. Go and see it.",
    go: 'Go and see ›',
    later: 'Later',
    tapAnywhere: 'or tap anywhere to keep going',
  },

  crate: {
    title: 'Crate',
    counts: (found: number, want: number) => `${found} found · ${want} want`,
    filters: ['All', 'Food', 'Onsen', 'Snow', 'Want to go'],
    emptyTitle: 'Nothing in here yet',
    emptyLines: ['Places you visit turn into toys.', 'Flag a few to go to first.'],
    ghostsLabel: (n: number) => `${n} toys to find on this trip`,
    ideas: (n: number) => `Ideas ${n}`,
    want: '+ Want to go',
    foundList: 'Places you found',
    // WI-13e: the collectibles shelf (mockup rw04).
    shelfTab: 'Shelf',
    findsTab: (n: number) => (n ? `Finds · ${n}` : 'Finds'),
    collected: (n: number) => `${n} collected`,
    sets: 'Sets',
    setChip: (set: string, have: number, of: number) => `${set} ${have}/${of}`,
    shelfLabel: (set: string, have: number, of: number) => `${set} shelf: ${have} of ${of} collected`,
    notYet: 'Not found yet',
    finishSet: (set: string) => `Finish the ${set} set`,
    setComplete: (set: string) => `${set} set complete!`,
    toGo: (n: number) => `${n} to go · a full set pays a bonus act key`,
    goldNote: 'Three the same turn gold',
    stageCaption: 'Places you visit turn into toys. Tap a ? for a hint.',
    // B10 (mockup tb01): the trip books on the shelf.
    booksTab: '📖 Books',
    booksNow: 'NOW',
    booksLabel: (n: number) => `Trip books shelf: ${n === 1 ? '1 book' : `${n} books`}. Tap one to read it.`,
    booksCaption: 'Each trip becomes a book: the places you found, the acts you opened, the words you learned. Tap one to read it.',
    booksNext: 'The next one is waiting for your next trip.',
    bookSpine: (title: string, year: string, now: boolean) => `${title}${year ? ` ${year}` : ''}${now ? ', this trip' : ''}`,
    openBook: (title: string, year: string) => `Open ${title}${year ? ` · ${year}` : ''}`,
    booksLoading: 'Fetching your trips…',
    /** Hint for a ghost toy: where to find it. */
    hint: (names: string[]) =>
      names.length <= 2 ? `Find it at ${names.join(' or ')}` : `Find it at ${names.slice(0, 2).join(', ')} or ${names.length - 2} more`,
  },

  data: {
    title: LABEL.data,
    export: LABEL.export,
    exportSub: 'A JSON file: people, phones, trips and every event the server holds. No PINs.',
    /** The destructive rows sit under their own heading (WI-16). */
    account: 'Account',
    delete: 'Delete the family',
    deleteSub: 'Removes everyone’s data from the server',
    what: 'This deletes every answer, play, prize, place and plan on the server, unlinks every phone and signs everyone out. The children’s names are removed. It can’t be undone: download everything first if you want a copy.',
    typeIt: 'Type DELETE to confirm',
    deleteNow: 'Delete the family’s data',
    // Just me (WI-37): the same, in the first person.
    titleSolo: 'My data',
    deleteSolo: 'Delete my data',
    deleteSoloSub: 'Removes your data from the server',
    whatSolo: 'This deletes every answer, play, prize, place and plan on the server, unlinks every phone and signs you out. It can’t be undone: download everything first if you want a copy.',
    confirmPin: 'Enter your PIN to delete',
    offline: 'Deleting needs signal.',
    setPinFirst: 'Set your PIN first: Settings › Parent PIN.',
  },

  /** Explorer's cards from the grown-up's own facts (B6). */
  explorer: {
    factContext: 'A fact from your grown-up',
  },

  /** Organiser → Trips → Facts for Explorer (B6). */
  facts: {
    title: 'Facts for Explorer',
    sub: (n: number) => (n ? `${n} fact${n === 1 ? '' : 's'}. Explorer asks them as cards.` : 'Explorer asks these as cards, on trips with no language to learn.'),
    question: 'Question',
    questionHint: 'e.g. What is the tallest mountain we will see?',
    answer: 'Right answer',
    wrong: (i: number) => `Wrong answer ${i} (optional)`,
    add: 'Add the fact',
    remove: (q: string) => `Remove: ${q}`,
    added: 'Added. Explorer asks it next time.',
    needOne: 'Add a wrong answer, or a second fact, so there is something to choose from.',
  },

  trips: {
    title: 'Trips',
    // Who's coming (B9).
    coming: "Who's coming",
    // WI-42 copy (WI-30).
    comingSub: 'Untick a child to let them follow the trip without playing it.',
    comingSet: (n: number, all: number) => (n === all ? 'Everyone is coming' : `${n} of ${all} children coming`),
    active: 'Shown on every phone.',
    other: 'Its Crate, prizes and plays are kept',
    activeTag: 'ACTIVE',
    switch: 'Switch to it',
    /** The row's button (WI-40 #3: room for the trip's name); its label stays "Switch to it". */
    switchShort: 'Switch',
    confirm: (title: string) => `Switch the family to ${title}? Every phone shows it next time it opens. Learning carries over; each trip keeps its own Crate and prizes.`,
    switched: (title: string) => `Switched to ${title}`,
    offline: 'Switching trips needs signal.',
    note: 'Each trip keeps its own Crate, prizes and plays; learning carries over.',
    built: 'Built into the app',
    made: (dates: string) => `Made in the app · ${dates}`,
    add: 'New trip',
    copy: 'Copy',
    copyLabel: (title: string) => `Copy ${title}`,
    // WI-40 #3: the row's ⋯ menu (Copy now; Rename and Delete later).
    more: (title: string) => `More for ${title}`,
    copyItem: 'Copy this trip',
    copyTitle: 'Copy a trip',
    copySub: (title: string) => `From ${title}: every stage, journey and plan, moved to the new dates`,
    copyStart: 'Starts on',
    copyNote: (days: number) => `${days} days, like the original. Nobody sees it until you switch to it.`,
    copyGo: 'Make the copy',
    machine: 'Reward machine',
    machineSub: (cabinet: string) => `This trip: the ${cabinet}. Plays and prizes stay the same.`,
    // WI-40 #1: one short word per segment; the subtitle says which machine the kit's is.
    machineKit: "Kit's",
    machineKitLabel: (cabinet: string | null) => (cabinet ? `Kit's: the ${cabinet}` : "Kit's default"),
    machineName: { gashapon: 'Gashapon', chest: 'Chest', claw: 'Claw' } as Record<string, string>,
    machineSet: (cabinet: string) => `Reward machine: the ${cabinet}`,
    // WI-42: the reward machine and facts fold away; the summary says what's chosen now.
    moreOptions: 'More options',
    factsNone: 'none yet',
    moreSub: (cabinet: string, facts: string) => `Reward machine: the ${cabinet} · Facts: ${facts}`,
  },

  newTrip: {
    // B9: start from a calendar file (a booking confirmation, a shared calendar).
    // Not "from": the date field above is labelled From (and tests find fields by label).
    fromIcs: 'Or start with a calendar file (.ics) or a backup (.json)',
    backupFound: (stages: number, plans: number) =>
      `A backup: ${stages} stage${stages === 1 ? '' : 's'} and ${plans} plan${plans === 1 ? '' : 's'} come across, moved to the dates above.`,
    icsFound: (n: number) => `${n} event${n === 1 ? '' : 's'} found: the dates are set, and they become plans on the new trip.`,
    where: 'Where are you going?',
    when: 'Name and dates',
    step: (n: number) => `New trip · step ${n} of 2`,
    kitLine: (season: string, islands: number, places: number, language: string) => `${season[0]?.toUpperCase()}${season.slice(1)} · ${fmt.plural(islands, 'island')} · ${fmt.plural(places, 'place')} · ${language}`,
    fullKit: 'FULL KIT',
    elsewhere: 'Somewhere else',
    // WI-19/WI-30: for the parent, not the developer.
    elsewhereSub: 'Anywhere in the world: find the place and plan the days. No language cards for it yet; the Arcade asks about the trip itself.',
    later: 'LATER',
    name: 'Trip name',
    namePlaceholder: 'Easter in the snow',
    from: 'From',
    until: 'Until',
    timezone: 'Time zone there',
    after: 'It starts as one stage over the whole trip: split it into stages and add plans in the itinerary. It isn’t shown to anyone until you switch to it.',
    create: 'Make the trip',
    back: 'Pick another place',
    made: (title: string) => `${title} is ready. Switch to it when you want the family to see it.`,
  },

  stageEdit: {
    island: 'Island',
    islandGeneric: 'Anywhere in Japan',
    islandNamed: 'Places',
    islandElsewhere: 'Outside Japan',
    suggested: (title: string) => `Suggested island: ${title}`,
    zoneThere: (zone: string, trip: string) => `Time zone there: ${zone}. While you're there, the trip's clock and surprises run on it (the trip starts on ${trip}).`,
    lat: 'Pin: latitude',
    lng: 'Pin: longitude',
    found: (place: string) => `Found: ${place} ✓`,
    pinFrom: (place: string) => `Pin: at ${place} until you find a place`,
    pinKept: (place: string) => `Pin: ${place} ✓`,
    editPin: 'Edit pin',
    newStageNote: 'A new stage starts the day after the trip’s last day.',
    edit: 'Edit stage',
    editLabel: (name: string) => `Edit stage: ${name}`,
    newStage: 'New stage',
    addStage: '+ Stage',
    name: 'Name',
    from: 'From',
    until: 'Until',
    like: 'Same place and island as',
    colour: 'Colour',
    colours: { blue: 'Blue', snow: 'Snow', teal: 'Teal', mint: 'Mint', yellow: 'Yellow', red: 'Red' } as Record<string, string>,
    surprise: 'A surprise stage',
    surpriseSub: 'Wrapped for the children until it opens',
    teaser: 'What the kids see',
    teaserDefault: 'Somewhere new',
    opens: 'Opens on',
    clue: 'Clue (optional)',
    save: 'Save',
    add: 'Add the stage',
    remove: 'Remove stage',
    removeConfirm: (name: string) => `Remove ${name}? The stage before it takes its days.`,
    why: {
      order: 'A stage has to end on or after the day it starts.',
      squeezed: 'That would leave the next stage with no days. Change that one first.',
      last: 'A trip needs at least one stage.',
    } as Record<string, string>,
    how: 'How',
    kinds: { flight: '✈ Flight', train: '🚆 Train', bus: '🚌 Bus', lift: '🚡 Lift', car: '🚗 Car', walk: '🚶 Walk', ferry: '⛴ Ferry' } as Record<string, string>,
    legFrom: 'From',
    legTo: 'To',
    leaves: 'Leaves',
    arrives: 'Arrives',
    arrivesDay: 'Arrives on',
    ref: 'Booking ref',
    notes: 'Note',
    booked: 'Booked',
    bookedSub: 'Tickets bought: shows the mint BOOKED tag',
    timesNote: 'Times as on the ticket, where it leaves and where it lands.',
    legOrder: 'It has to arrive after it leaves.',
    addLeg: 'Add the journey',
    addTravel: '+ Travel',
    editLeg: 'Edit journey',
    newLeg: (day: string) => `Travel on ${day}`,
    removeLeg: 'Remove journey',
    removeLegConfirm: 'Remove this journey?',
  },

  // "See it as <child>" (WI-19): the trip as one child sees it, from Organiser home.
  seeAs: {
    open: (name: string) => `See it as ${name}`,
    button: '👁',
    title: (name: string) => `As ${name} sees it`,
    sub: (name: string) => `What ${name}'s phone shows today. Change it in Family › ${name} › Controls.`,
    notGoing: (name: string) => `${name} isn't on this trip: they follow it, read-only.`,
    rules: 'Their rules',
    stages: 'The trip, today',
    wrapped: (real: string) => `really ${real}`,
    hiddenPlans: (n: number) => `${n} plan${n === 1 ? '' : 's'} hidden`,
    ahead: (n: number) => `${n} more stage${n === 1 ? '' : 's'} not shown yet`,
  },

  rules: {
    learn: (goal: number, tracks: number, difficulty: string) => `Learn: ${goal} right answers a day · ${fmt.plural(tracks, 'track')} · ${difficulty}`,
    visibility: {
      all: () => 'You see the whole trip (except surprises)',
      window: (days: number) => `You see plans for today and the next ${fmt.days(days)}`,
      stage: () => 'Each new place opens when you get there',
      mystery: () => 'Full mystery: places open on their reveal day',
    } as Record<string, (days: number) => string>,
    plays: (n: number) => `Most plays a day: ${n}`,
    suggestOn: 'You can add want-to-go places',
    suggestOff: "Want-to-go places are off for now",
    edit: { no: 'Grown-ups make the plans', own: 'You can add and change your own plans', unlocked: 'You can change any plan that isn’t locked' } as Record<string, string>,
    changed: (who: string, what: string) => `${who} changed your rules: ${what}`,
    title: 'My rules',
    seeTitle: (who: string) => `What ${who} can see`,
    see: 'Your answers, plays and prizes, your want list, your plans, and whether you were at a place you checked in at. Never where you are.',
    open: 'My rules',
    openSub: 'Set by a grown-up. Ask them if one feels wrong.',
  },

  learn: {
    title: 'Learn',
    island: 'A snowy island with a chalkboard, and your figure waving',
    /** "Today's new sounds" (WI-09): the next characters to meet, and the warm-up that brings them in. */
    newSounds: "Today's new sounds",
    newSoundsCount: (n: number) => `${n} NEW`,
    newSoundsSub: "Tap one to hear it. Then a quick warm-up: you hear a sound and pick its character. Right answers count toward today's goal.",
    newSoundHear: (ch: string) => `Hear ${ch}`,
    warmUp: (n: number) => `Warm up: ${n} new sound${n === 1 ? '' : 's'}`,
    streak: (n: number) => `🔥 ${fmt.plural(n, 'day')}`,
    goalTitle: "Today's goal",
    // Only right answers count toward the goal (WI-08), so the goal is in right answers (WI-11).
    goalLeft: (n: number) => `${fmt.plural(n, 'more right answer')} for a play in the Arcade`,
    goalLeftNoPlay: (n: number) => `${fmt.plural(n, 'more right answer')} to go`,
    goalDone: 'Done for today. Keep going if you like!',
    earnsPlay: (n: number) => `+${n} PLAY`,
    earned: 'PLAY EARNED ✓',
    goalLabel: "Today's goal",
    goalLabelOf: (done: number, goal: number) => `Today's goal: ${done} of ${goal} right answers`,
    goalPill: (done: number, goal: number) => `${Math.min(done, goal)}/${goal}`,
    tracksHead: 'Tracks',
    tracks: { everyday: 'Everyday words', script: 'The script', together: 'Putting it together', numbers: 'Numbers and prices' } as Record<string, string>,
    script: (deck?: string) => (deck ? `${deck}, one sound at a time` : 'The script'),
    // One progress model (WI-10): met = answered once, learned = remembered over days.
    progress: (met: number, learned: number, total: number) => `${met} met · ${learned} learned · ${total} in all`,
    progressLabel: (title: string, met: number, learned: number, total: number) => `${title}: ${met} of ${total} met, ${learned} learned`,
    unlocksAt: (n: number) => `Real words from signs and shelves · opens after ${n} characters`,
    scriptUnlocksAt: (n: number, first?: string) => `Opens once you've met all ${n} basic ${first ?? 'characters'}`,
    level: (n: number) => `Lv ${n}`,
    xp: (xp: number, next?: number) => (next === undefined ? `${xp} XP · top level` : `${xp} / ${next} XP`),
    xpLabel: (level: number, xp: number, next?: number) => `Level ${level}: ${xp} XP${next === undefined ? '' : `, next level at ${next}`}`,
    soundsHead: 'Heard on the island',
    soundsSub: 'Sound words you collected on the island and in the Arcade. Tap one to hear it.',
    soundHear: (word: string, meaning: string) => `${word}, ${meaning}: hear it`,
    noTracks: 'No tracks switched on. A grown-up can choose them in your controls.',
    start: (track: string) => `Start: ${track}`,
    continue: (track: string, left?: number) => `Continue: ${track}${left ? ` · ${left} to go` : ''}`,
    practise: 'Goal done · practise anyway',
    /** What each track asks, under its name (WI-11, L2-08). */
    blurbs: {
      everyday: 'Hear a phrase, pick what it means',
      script: 'Pick the sound each character makes',
      together: 'Read real words from signs and shelves',
      numbers: 'Read and hear numbers, prices and times',
    } as Record<string, string>,
    free: 'Learning is free: no plays needed. Reaching the goal earns a play to spend in the Arcade.',
    sessionDone: 'Nice!',
    goalMet: 'Goal done!',
    metSub: (plays: number, days: number) => `+${fmt.plural(plays, 'play')} in the Arcade${days > 1 ? ` · ${days} days in a row` : ''}`,
    /** The session summary (WI-11): right answers toward the goal, not "n of m" (re-asks made m grow). */
    summaryRight: (n: number) => (n ? `+${fmt.plural(n, 'right answer')} toward today's goal` : 'No right answers this time: they come back soon'),
    summaryXp: (n: number) => `+${n} XP`,
    summaryCards: 'Cards this session',
    summaryOk: (card: string) => `${card}: right`,
    summaryMiss: (card: string) => `${card}: try again`,
    summaryMet: (cards: string) => `New today: ${cards}`,
    summaryAgain: (cards: string) => `Try again next time: ${cards}`,
    toArcade: 'To the Arcade',
    keepLearning: 'Keep learning',
  },

  place: {
    toy: 'Check in here to find its toy',
    found: 'Found! It’s a toy in your Crate',
    want: '☆ Want to go',
    wanted: '★ On my list',
    maps: 'Open in Maps',
  },

  myPlan: {
    add: (day: string) => `Add to ${day}`,
    edit: 'My plan',
    what: 'What',
    when: 'When (optional)',
    where: 'Where (from your want list)',
    addIt: 'Add',
    save: 'Save',
    remove: 'Remove',
    note: 'Everyone on the trip sees your plan. Grown-ups can change it too.',
    addButton: (weekday: string) => `+ Add to ${weekday}`,
  },

  want: {
    title: 'Want to go',
    paste: 'Paste a map link',
    add: '+ Add to my want list',
    looking: 'Looking…',
    how: 'Copy the link from Google or Apple Maps (Share → Copy link). A grown-up sees it as an idea for the plan.',
    noPlace: "That link doesn't have a place in it. Try Share → Copy link in Maps.",
    fromLink: 'A place from a link',
    added: (name: string) => `${name} is on your want list`,
    mine: (n: number) => `My want list · ${n}`,
    found: 'Found! It’s a toy in your Crate',
    yours: 'From your link',
    idea: 'From the ideas',
    none: 'Nothing yet. Paste a link, or star an idea.',
    remove: (name: string) => `Take ${name} off my list`,
    star: (name: string) => `Want to go to ${name}`,
  },

  settings: {
    title: LABEL.settings,
    // Credits (WI-30): one place for what the app is built with, instead of a caption in the World panel.
    credits: 'Credits',
    creditsSub: 'Voices, fonts, weather',
    creditLines: [
      'Voices: VOICEVOX:ずんだもん (Zundamon); Korean voice: MeloTTS (MIT License)',
      'Fonts: Fredoka, Nunito, M PLUS Rounded 1c and Jua (SIL Open Font License: /licences/OFL.txt)',
      'Sound effects made with Stable Audio Open (Stability AI)',
      'Weather data from MET Norway (CC BY 4.0)',
      'Place search: GeoNames.org (CC BY 4.0)',
    ],
    onPhone: (role: string) => (role === 'organiser' ? 'Organiser on this phone' : 'Player on this phone'),
    switch: 'Switch',
    parentMode: (child: string) => `Parent mode on ${child}'s phone`,
    parentModeSub: `Back to them after ${PARENT_MODE_MINUTES} minutes without a tap`,
    parentModeDone: 'Done',
    syncNow: 'Sync now',
    glove: 'Glove mode',
    gloveSub: 'Bigger buttons, switches, fields and tab bar',
    sound: 'Sound',
    vibration: 'Vibration',
    motion: 'Motion',
    motionSub: 'Auto saves battery',
    motionChoices: { auto: 'Auto', calm: 'Calm', still: 'Still' },
    organiser: 'Organiser tools',
    organiserSub: 'Edit days, lock bookings, bonus plays',
    pin: 'Parent PIN',
    pinSolo: 'Your PIN',
    pinSet: 'Set · asked when picking you on a phone',
    pinMissing: 'Not set: anyone can pick you. Set one now.',
    pinChange: 'Change',
    pinCreate: 'Set PIN',
    family: LABEL.family,
    familySub: 'Children, figures, linked phones',
    familyOpen: 'Manage family',
    familySolo: 'My phones',
    familySoloSub: 'Your figure, linked phones, your data',
    familySoloOpen: 'Manage my phones',
    phonesOpen: 'Manage',
    pack: (title: string, v: number) => `Trip pack: ${title} · v${v} · works offline`,
    soundsReady: 'Island sounds: ready offline ✓',
    noVoice: 'No voice on this phone for the trip’s language: cards without a recording show their words instead. Add the voice in the phone’s text-to-speech settings.',
    soundsLoading: (done: number, total: number) => `Island sounds: downloading ${done} of ${total}…`,
    soundsPartial: (done: number, total: number) => `Island sounds: ${done} of ${total} saved · the rest next time there's signal`,
  },

  sync: {
    synced: (time: string) => `Synced · ${time}`,
    // WI-40 #8: a time alone was read as today's the next morning.
    syncedNow: 'Synced just now',
    syncedYesterday: (time: string) => `Synced yesterday ${time}`,
    syncedDaysAgo: (n: number) => `Synced ${n} days ago`,
    never: 'Not synced yet',
    queued: (n: number) => `Queued ${n}`,
    offline: (time: string) => `Offline since ${time}`,
    banner: (n: number, time: string | null) =>
      n ? `Saved on your phone · ${fmt.plural(n, 'change')} waiting` : `Saved on your phone${time ? ` · synced ${time}` : ''}`,
    offlineTag: 'OFFLINE',
  },

  tier: { calm: 'Calm mode', still: 'Still mode' },

  family: {
    title: 'Family',
    children: (n: number, max: number) => `Children · ${n} of ${max}`,
    grownUps: 'Grown-ups',
    addChild: 'Add a child',
    addAdult: 'Add a grown-up',
    addAdultSub: "Sees the plan, OKs prizes. Can't change the family.",
    full: 'Full',
    parent: 'Parent (admin)',
    adult: 'Grown-up',
    pinOn: 'PIN set',
    pinOff: 'no PIN yet',
    phones: LABEL.phones,
    phonesSub: 'Link a new phone, see whose phone is whose',
    // A grown-up's own sheet (WI-16): their role and PIN.
    role: 'Role',
    pinRow: 'PIN',
    pinYours: 'Your PIN',
    pinTheirs: (name: string) => `${name} sets it on their own phone, on Organiser home.`,
    // Running the family (B15): co-admin and hand-over. Passkeys come later.
    running: 'Running the family',
    parentTag: 'Parent',
    makeCo: 'Make co-admin',
    makeCoSub: (name: string) => `${name} can change the family, phones and prizes too. You both stay in charge.`,
    makeCoBtn: 'Make',
    handOver: 'Hand over the parent role',
    handOverSub: (name: string) => `${name} becomes the parent; you become a grown-up.`,
    handOverBtn: 'Hand over',
    revoke: 'Turn back into a grown-up',
    revokeSub: (name: string) => `${name} stops being a parent. You stay one.`,
    revokeBtn: 'Turn back',
    needsPin: (name: string) => `${name} needs a PIN of their own first.`,
    adminPin: 'Both need your PIN.',
    handOverTitle: (name: string) => `Hand over to ${name}?`,
    handOverWhat: (name: string) =>
      `${name} becomes the family's parent: the family, phones, prizes, the recovery code and deleting the family. You stay in the family as a grown-up who can plan and OK prizes.`,
    handOverWarn: (name: string) => `Only ${name} can give it back.`,
    coTitle: (name: string) => `Make ${name} a co-admin?`,
    coWhat: (name: string) => `${name} can do everything you can, including handing the role over. You can turn them back later.`,
    adminConfirmPin: 'Enter your PIN to go on',
    adminDone: (name: string) => `${name} is a parent now`,
    adminBack: (name: string) => `${name} is a grown-up again`,
    handedOver: (name: string) => `${name} is the parent now`,
    adminProblem: {
      'needs a pin': 'They need a PIN of their own first.',
      'last parent': 'A family keeps at least one parent.',
    } as Record<string, string>,
    noPin: 'No PIN yet',
    titleSolo: 'My phones',
    you: 'You',
    addSomeone: 'Travelling with someone?',
    plays: 'Plays too',
    playsSub: 'Earns plays, wins prizes, has a Learn goal',
    playsTag: 'plays',
    ages: { u8: 'Under 8', '8-12': '8–12', '13+': '13+' } as Record<string, string>,
    // The configurator (mockup o05).
    newChild: 'New child',
    newAdult: 'New grown-up',
    name: 'Name',
    age: 'Age',
    coat: 'Coat',
    hat: 'Hat',
    hatColour: 'Hat colour',
    scarf: 'Scarf',
    scarfNone: 'No scarf',
    holding: 'Holding',
    hats: { beanie: 'Beanie', cap: 'Cap', hood: 'Hood', earmuffs: 'Earmuffs', none: 'None' } as Record<string, string>,
    /** Figure swatch names for screen readers (shared/figures COATS and ACCENTS), by hex. */
    colours: { ff4f4f: 'Red', f5b21e: 'Yellow', '1f8a3a': 'Green', '43c6db': 'Teal', '2f7bff': 'Blue', '9b6bff': 'Purple', ff7ab6: 'Pink', '1b2a3a': 'Navy', ffffff: 'White' } as Record<string, string>,
    props: { board: '🏂 Board', skis: '⛷ Skis', sled: '🛷 Sled', camera: '📷 Camera', clipboard: '📋 Clipboard', none: 'Nothing' } as Record<string, string>,
    save: 'Save',
    remove: (name: string) => `Remove ${name}`,
    removeConfirm: (name: string) => `Remove ${name} from the family? Their sticker and crate history stays; they just can't sign in.`,
    privacy: 'Only a first name (or nickname) and an age band. No birthday, photo or email.',
    spin: 'Drag to spin · double-tap to jump',
    confirmPin: 'Enter your PIN to remove someone',
  },

  organiser: {
    open: 'Organiser home',
    openSub: 'PIN, family, phones, itinerary, rewards',
    openSubSolo: 'PIN, phones, itinerary, rewards',
    title: 'Organiser',
    tripLine: (days: number, stages: number, journeys: number, plans: number) =>
      `${count(days, 'day')} · ${count(stages, 'stage')} · ${count(journeys, 'journey')} · ${count(plans, 'plan')}`,
    kit: (title: string) => `Kit: ${title}`,
    needsYou: 'Needs you',
    nothing: 'Nothing waiting. 🎉',
    prizeWon: (name: string, prize: string) => `${name} won “${prize}”`,
    prizeSub: 'Arcade prize · cash it in when you can',
    ok: 'OK',
    later: 'Later',
    activity: (name: string, title: string) => `${name}: ${title}`,
    activitySub: (plays: number, date: string) => `Done? Award ${plays} play${plays === 1 ? '' : 's'} · ${date}`,
    cashedFor: (name: string, prize: string) => `Cashed in for ${name}: ${prize}`,
    taskSub: (plays: number) => `Holiday task done? Award ${plays} play${plays === 1 ? '' : 's'}`,
    yes: 'Yes',
    suggestion: (name: string, place: string) => `${name} wants: ${place}`,
    suggestionSub: 'Want-to-go · add it to a day?',
    // A grown-up without a PIN (WI-16).
    noPinMe: 'No PIN yet: you',
    noPinMeSub: 'Needed to pick you on a child’s phone and for big changes',
    noPinOther: (name: string) => `No PIN yet: ${name}`,
    noPinOtherSub: (name: string) => `${name} can’t sign in on a child’s phone until they set one in Settings`,
    setPin: 'Set PIN',
    plan: 'Plan',
    notNow: 'Not now',
    today: 'Today',
    todayLine: (cards: number, goal: number, streak: number) => `${cards}/${goal} cards${cards >= goal ? ' ✓' : ''} · 🔥 ${streak}`,
    playsLeft: (n: number) => (n === 1 ? '1 play' : `${n} plays`),
    itinerary: '🗓 Itinerary',
    family: '👨‍👧‍👦 Family',
    familySolo: '📱 My phones',
    prizes: '🎁 Prizes',
    trips: '🧳 Trips',
    rewards: '🎁 Rewards',
    rewardsOpen: 'Rewards',
    awarded: (n: number, name: string) => `+${n} for ${name}`,
    // The set-up checklist (WI-19, mockup parent-home p01).
    setupTitle: `Set up ${APP_NAME}`,
    setupCount: (done: number, all: number) => `${done} of ${all}`,
    setupSub: 'A few minutes now, then everyone can start.',
    setupSoloSub: 'A few minutes now, then you can start.',
    setupDone: '✓ Set up',
    setupFamilyDone: (solo: boolean) => (solo ? 'PIN, recovery code, phones ✓' : 'PIN, recovery code, children, phones ✓'),
    tripReady: 'Trip ready',
    skip: 'Skip for now',
    steps: {
      pin: { title: 'Your PIN', todo: 'Guards new phones and big changes', done: 'Set · guards new phones and big changes', go: 'Set' },
      recovery: { title: 'Recovery code', todo: 'The way back in if every phone is lost', done: 'Saved · the way back in if every phone is lost', go: 'Make' },
      children: { title: 'Add the children', todo: 'A nickname, an age band and a figure', done: 'Added', go: 'Add' },
      phones: { title: 'Link their phones', todo: 'A code or QR on each phone', done: 'Linked', go: 'Link' },
      trip: { title: 'Create the trip', todo: 'Where, and the dates', done: 'Made', go: 'Start' },
      // WI-42: the trip's own steps ("Trip ready n/6").
      stays: { title: 'Stays added', todo: 'Where you sleep, with check-in and check-out', done: 'Added', go: 'Add' },
      travel: { title: 'Travel between stays', todo: 'One tap per change-over: train, bus, car…', done: 'Every change-over has a journey', go: 'Add' },
      bookings: { title: 'Bookings', todo: 'Booking refs, or tick each journey booked', done: 'Every journey booked', go: 'Open' },
      names: { title: 'Name every stop', todo: 'A stop still has a stand-in name, like “Base town”', done: 'Every stop named', go: 'Name it' },
      rewards: { title: 'Set rewards and tasks', todo: 'Prizes to win, jobs worth plays', done: 'Set', go: 'Set' },
    } as Record<string, { title: string; todo: string; done: string; go: string }>,
    phonesSolo: 'Link your other phones',
    skipped: 'Skipped for now',
    you: 'You',
    pinRow: 'Your PIN',
    pinSet: 'Set',
    pinMissing: 'Not set',
    pinChange: 'Change',
    recoveryRow: 'Recovery code',
    recoveryRowSub: 'Make a new one (the old one stops working)',
    recoveryNew: 'New code',
    recoverySaved: "I've saved it",
  },

  prizes: {
    title: 'Prizes',
    add: '+ Add',
    realWorld: 'Real-world',
    fromMachine: (w: MachineWords) => `From the ${w.cabinet}`,
    needsOk: 'needs your OK',
    who: { all: 'everyone', u8: 'under 8', '8-12': '8–12', '13+': '13+ only' } as Record<string, string>,
    left: (n: number) => (n === 1 ? '1 left this trip' : `${n} left this trip`),
    noLimit: 'no limit',
    paused: 'paused',
    rarity: { common: 'COMMON', rare: 'RARE', legendary: 'LEGENDARY' } as Record<string, string>,
    // WI-14: a note the child sees when they tap the prize (what it is, any rules).
    noteField: 'Note for the child',
    notePlaceholder: 'e.g. Up to ¥500, at the next dinner',
    earning: 'How plays are earned',
    perChild: 'most a day: per child',
    daily: 'Daily Learn goal done',
    dailySub: (n: number) => `${n} play · before and during the trip`,
    checkin: 'Check in at a planned place',
    checkinSub: (n: number, m: number) => `${n} play · phone within ${m} m`,
    activity: 'Activity done',
    activitySub: 'Plays set on the plan · you confirm',
    free: 'A free play every day',
    freeSub: 'Keeps the Arcade open before anything is earned · doesn’t roll over',
    // WI-13: the twist itself is the reward (owner, 23 Oct: "you get a vowel quiz, not a reward").
    chance: (w: MachineWords) => `Each ${w.one} opens straight onto one reward: mostly a collectible for the shelf, a new act for the island when it's time, and sometimes one of these prizes (how often: Rewards).`,
    note: 'Real-world prizes never pay out by themselves: the child sees “Ask a grown-up to cash this in”, and you get it in Needs you.',
    edit: 'Edit prize',
    newPrize: 'New prize',
    name: 'Prize',
    emoji: 'Emoji',
    rarityField: 'How rare',
    whoField: 'Who can win it',
    cap: 'How many this trip',
    capNone: 'Any',
    save: 'Save',
    remove: 'Remove prize',
  },

  // WI-13f: the parent's one Rewards screen (mockup docs/renders/rewards3d/rw05-parent.png).
  rewards: {
    title: 'Rewards',
    open: 'Rewards',
    forWho: 'For',
    defaults: (age: string) => `defaults for ${age}`,
    maxPlays: 'Twists a day, at most',
    earning: (name: string) => `How ${name} earns twists`,
    daily: 'Daily twist',
    dailySub: 'One free twist every day',
    learn: 'Learn goal',
    learnSub: 'Right answers only',
    tasks: 'Holiday tasks',
    tasksSub: (n: number, self: number) => `${n} tasks · ${self} ticked by the child`,
    checkin: 'Check-ins on the trip',
    checkinSub: 'Near a planned place',
    prizes: 'Real prizes',
    prizeList: (n: number) => `${n} in the list ›`,
    levels: { off: 'Off', rarely: 'Rarely', sometimes: 'Sometimes', often: 'Often' } as Record<string, string>,
    more: 'More',
    moreSub: 'How much opens before the trip',
    before: 'Before the trip, open',
    beforeLevels: { little: 'A little', some: 'About 40 %', most: 'Most' } as Record<string, string>,
    moreNote: 'Keys open new acts on each child’s own islands: at most 1 a day before the trip, 2 a day during it. A full collectible set pays a bonus key.',
    taskList: 'Holiday tasks',
    addTask: '+ Add',
    taskSelf: (n: number) => `The child ticks it · +${n}`,
    taskConfirm: (n: number) => `You confirm · +${n}`,
    self: 'SELF',
    youOk: 'YOU OK',
    when: { before: 'before', during: 'during', any: 'any time' } as Record<string, string>,
    newTask: 'New task',
    editTask: 'Edit task',
    taskName: 'Task',
    taskEmoji: 'Emoji',
    taskPlays: 'Twists it earns',
    taskWho: 'Who ticks it',
    taskWhoSelf: 'The child',
    taskWhoYou: 'A grown-up',
    taskWhen: 'When',
    save: 'Save',
    remove: 'Remove task',
  },

  controls: {
    open: 'Controls',
    openSub: 'Learn, how far ahead, plays',
    title: (name: string) => `${name}'s controls`,
    from: (band: string) => `Started from the ${band} defaults`,
    learn: 'Learn',
    tracks: { everyday: 'Everyday words', script: 'Reading the script', together: 'Putting it together', numbers: 'Numbers and prices' } as Record<string, string>,
    tracksSub: { everyday: 'Hello, thank you, how much…', script: 'The script, one character at a time', together: 'Unlocks after 20 characters', numbers: 'Prices, times and counting' } as Record<string, string>,
    /** A script track by its deck ("Reading Katakana"), WI-10. */
    scriptTrack: (deck: string) => `Reading ${deck}`,
    scriptSub: 'One character at a time',
    scriptLaterSub: (n: number, first: string) => `Opens after all ${n} basic ${first}`,
    goal: 'Daily goal',
    // The goal counts right answers (WI-08).
    goalN: (n: number) => `${n} right`,
    difficulty: 'Difficulty',
    difficulties: { gentle: 'Gentle', standard: 'Standard', stretch: 'Stretch' } as Record<string, string>,
    // WI-30 (L4-12): what the goal and each difficulty actually do.
    goalSub: 'Right answers a day; reaching it earns a play',
    difficultySub: {
      gentle: 'Three answers to pick from instead of four',
      standard: 'Four answers to pick from',
      stretch: 'Five answers to pick from',
    } as Record<string, string>,
    trip: 'Trip',
    ahead: 'How far ahead',
    canSuggest: 'Can add want-to-go places',
    canSuggestSub: 'They reach you as suggestions',
    canEdit: 'Can change the plan',
    canEdits: { no: 'No', own: 'Own plans', unlocked: 'Unlocked' } as Record<string, string>,
    arcade: 'Arcade',
    maxPlays: 'Most plays a day',
    maxPlaysSub: 'Earned plays above this wait for tomorrow',
    // How far ahead (mockup o07).
    aheadTitle: (name: string) => `How far ahead can ${name} see?`,
    modes: { all: 'Everything', window: 'Today and the next days', stage: 'Stage by stage', mystery: 'Full mystery' } as Record<string, string>,
    modesSub: {
      all: 'The whole plan, all the way to the flight home. Surprises stay wrapped.',
      window: 'Later days show the town name only',
      stage: 'Each place is revealed when you arrive',
      mystery: 'Wrapped gifts with a countdown. You pick each reveal date.',
    } as Record<string, string>,
    days: (n: number) => (n === 1 ? 'Today + tomorrow' : `Today + ${n} days`),
    revealOn: 'Opens on',
    sees: (name: string, date: string) => `${name} sees on ${date}`,
    seesStages: (shown: string, hidden: number) => (hidden ? `${shown} · ${hidden} wrapped` : shown),
    seesPlans: (journeys: number, plans: number) => `${count(journeys, 'journey')} · ${count(plans, 'plan')} visible`,
    changed: 'Saved. They see it next time the phone syncs.',
    undo: 'Undo',
    undone: 'Put back as it was.',
  },

  plan: {
    move: 'Drag to move (or use the arrow keys)',
    moved: (time: string) => `Moved: now at ${time}`,
    movedAnyTime: 'Moved',
    title: 'Itinerary',
    open: 'Itinerary',
    openSub: 'Plan days, lock bookings, surprises',
    back: 'Back to the map',
    stages: 'Stages',
    day: (n: number, date: string) => `Day ${n} · ${fmt.day(date)}`,
    before: (date: string) => `Before the trip · ${fmt.day(date)}`,
    add: (n: number) => `+ Add to Day ${n}`,
    empty: 'Nothing planned yet. Add a place, paste a Maps link, or type something.',
    booked: 'BOOKED',
    surprise: 'SURPRISE',
    locked: 'Locked for children',
    anyTime: 'Any time',
    queued: (n: number) => `${n === 1 ? '1 change' : `${n} changes`} waiting · they sync when there's signal`,
    // The item editor (mockups o10, w01).
    newItem: (date: string) => `Add to ${fmt.day(date)}`,
    editItem: 'Edit plan',
    fromLibrary: 'From the library',
    pasteLink: 'Paste a link',
    justText: 'Just text',
    search: (n: number) => `Search ${n} places`,
    link: 'Google or Apple Maps link',
    linkLooking: 'Looking it up…',
    linkOffline: 'Short links need signal. Long ones work offline.',
    linkNothing: "Couldn't find a place in that link.",
    linkView: 'Pin is the map’s centre: check it',
    newPlace: 'NEW PLACE',
    kind: 'Kind',
    kinds: { food: 'Food', onsen: 'Onsen', snow: 'Snow', poi: 'Sight', shop: 'Shop', activity: 'Activity', stay: 'Stay', transit: 'Transit' } as Record<string, string>,
    titleField: 'What',
    subField: 'Note',
    from: 'From',
    until: 'Until',
    bookedSub: 'Shows the mint BOOKED tag',
    lock: 'Lock for kids',
    lockSub: 'They can see it but not move it',
    surpriseField: 'Surprise',
    surpriseSub: 'Hidden until it happens',
    teaser: 'What the kids see',
    teaserDefault: 'Something special',
    /** P3: the reveal time is in trip time ("Reveal at (Tokyo time)"). */
    revealIn: (tz: string) => `Reveal at (${tz.split('/').pop()?.replace(/_/g, ' ')} time)`,
    award: 'Activity award',
    awardSub: (n: number) => (n ? `${n} play${n === 1 ? '' : 's'} when you confirm it's done` : 'No plays'),
    who: "Who's going",
    everyone: 'Everyone',
    change: 'Change',
    save: 'Save',
    addIt: 'Add',
    remove: 'Remove',
    removeConfirm: (title: string) => `Remove "${title}" from the plan?`,
    files: 'Import / export',
    exportJson: 'Export JSON (backup)',
    exportIcs: 'Calendar (.ics) for the grown-ups',
    importJson: 'Import (.json backup or .ics calendar)',
    imported: (n: number) => (n ? `Imported ${n} plan${n === 1 ? '' : 's'}` : 'Nothing new in that file'),
    importBad: `That file isn't a ${APP_NAME} backup or a calendar.`,
    removeExamples: (n: number) => `Remove example plans (${n})`,
    removeExamplesConfirm: (n: number) => `Remove all ${n} example plans? Your own plans stay.`,
    removedExamples: (n: number) => `Removed ${n} example plan${n === 1 ? '' : 's'}`,
    selectHint: 'Tap a plan to edit it, or add one.',
  },

  phones: {
    qr: 'QR code of the join link',
    qrSub: 'Or scan this with the new phone’s camera',
    title: LABEL.phones,
    link: LABEL.link,
    // Whose phone it is (WI-17); handing one over is always this explicit action.
    whose: (name: string) => `${name}'s phone`,
    nobodys: 'Nobody’s phone yet',
    makeIts: (name: string) => `Make this ${name}'s phone`,
    makeSub: `A grown-up picked on a child’s phone is in parent mode for ${PARENT_MODE_MINUTES} minutes. Hand a phone over here.`,
    madeIts: (name: string) => `This is ${name}'s phone now`,
    handPin: 'Enter your PIN to hand this phone over',
    linkSub: 'On the new phone, open the link or type the code. It works once, for 7 days.',
    code: 'Code',
    share: 'Share link',
    shareText: (code: string) => `Join our ${APP_NAME} family: open this link on your phone, or type the code ${code}.`,
    expires: (date: string) => `Expires ${date}`,
    thisPhone: 'This phone',
    lastSeen: (when: string) => `last seen ${when}`,
    // WI-30: a phone is called by whose it is and what it is ("Mia's Pixel 7"), with when it joined.
    nick: (name: string, label: string) => `${name}'s ${label}`,
    linkedOn: (date: string) => `linked ${date}`,
    nobody: 'Nobody signed in yet',
    unlink: 'Unlink',
    unlinkConfirm: (label: string) => `Unlink ${label}? Whoever is signed in there is signed out.`,
    offline: 'Needs signal.',
  },

  // WI-42: trip setup for a grown-up, stays first (mockups o13–o16, docs/mockups/trip-setup.html).
  stays: {
    open: '🛏 Stays',
    title: 'Where are you sleeping?',
    step: (n: number) => `Trip setup · ${n} of 2`,
    sub: 'Each stay becomes a stage. Dates stay back to back.',
    prompt: 'Start with where you sleep',
    promptSub: 'Add your stays and the trip fills in: stages, islands, then how you get between them.',
    promptGo: 'Add stays',
    route: (title: string) => `${title} route`,
    routeLine: (line: string, nights: string) => `${line} · ${nights} nights`,
    useRoute: 'Use this route, change dates',
    otherRoutes: 'Other routes',
    yours: 'Your stays',
    span: (from: string, to: string, n: number) => `${from} → ${to} · ${n} night${n === 1 ? '' : 's'}`,
    name: 'Hotel name or place',
    stayLabel: (name: string, checkIn: string) => `Stay from ${fmt.day(checkIn)}${name.trim() ? `: ${name.trim()}` : ''}`,
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    nights: (n: number) => `${n} night${n === 1 ? '' : 's'}`,
    island: 'Island',
    suggested: (title: string) => `Suggested island: ${title}`,
    remove: (name: string) => `Remove ${name || 'this stay'}`,
    add: '+ Add a stay',
    next: 'Next: how you get there',
    saved: (n: number) => `${n} stay${n === 1 ? '' : 's'} saved`,
    why: { empty: 'Add at least one stay.', unnamed: 'Give every stay a name.' } as Record<string, string>,
    outOfOrder: 'Check-out has to be after check-in.',
    howTitle: 'How do you get there?',
    howSub: 'One tap each. Times and booking refs can come later.',
    howNone: 'One stay, so no travel between stays. Add flights home and the like in the itinerary (+ Travel).',
    gap: (from: string, to: string) => `${from} → ${to}`,
    how: 'How',
    kinds: { train: '🚆', bus: '🚌', car: '🚗', flight: '✈️', ferry: '⛴️' } as Record<string, string>,
    kindNames: { train: 'Train', bus: 'Bus', car: 'Car', flight: 'Flight', ferry: 'Ferry' } as Record<string, string>,
    times: 'Times and booking ref (optional)',
    leaves: 'Leaves (optional)',
    arrives: 'Arrives (optional)',
    ref: 'Booking ref (optional)',
    saveTimes: 'Save times',
    timesSaved: 'Saved',
    more: (n: number) => `+${n} more journey${n === 1 ? '' : 's'} that day`,
    done: 'Done',
    untimed: 'no time yet',
  },
  // WI-42 #1: paste your bookings (mockup o15).
  paste: {
    open: '📋 Paste bookings',
    openIcs: '📅 Import bookings (.ics)',
    title: 'Paste your bookings',
    icsTitle: 'Import your bookings',
    icsSub: 'Pick a calendar file (.ics) from a hotel or airline: its stays and journeys come in for you to check. Pasting emails is coming soon.',
    ics: 'A calendar file (.ics)',
    icsNone: 'No stays or journeys found in that calendar. Its events can come in as plans: Itinerary › Import.',
    sub: 'Paste booking confirmations or an itinerary: hotels, flights, trains. You check everything before it’s added.',
    field: 'Booking emails or itinerary',
    placeholder: 'Paste the text of one or more confirmation emails here',
    read: 'Read it',
    reading: 'Reading…',
    failed: 'Couldn’t read that. Check the signal and try again, or add the stays by hand.',
    found: (stays: number, legs: number) => `Found ${stays} stay${stays === 1 ? '' : 's'}, ${legs} journey${legs === 1 ? '' : 's'}`,
    check: 'Check the yellow ones, untick anything wrong, then add.',
    stays: 'Stays',
    legs: 'Journeys',
    unsure: 'Check:',
    day: 'Day',
    leaves: 'Leaves',
    sharedOff: 'Reading shared emails is coming soon. For now, add the stays by hand or import a calendar file.',
    noDate: 'no date',
    noTime: 'no time found',
    outside: 'outside the trip',
    addAll: (n: number) => `Add all (${n})`,
    added: (stays: number, legs: number) => `Added ${stays} stay${stays === 1 ? '' : 's'} and ${legs} journey${legs === 1 ? '' : 's'}`,
    more: 'Paste more',
    privacy: `Only the text you paste and the trip’s dates are sent to Claude (Anthropic) to read. ${APP_NAME} doesn’t keep it, and nothing is added until you tap Add.`,
    privacyAfter: `Only the text you pasted was sent to Claude (Anthropic) to read. ${APP_NAME} doesn’t keep it.`,
  },
  // WI-42 #6: stops still carrying a seed or placeholder name.
  names: {
    title: 'Name this stop',
    sub: (name: string, n: number) => `${n} journey${n === 1 ? ' goes' : 's go'} to or from “${name}”. What's it really called?`,
    stage: (name: string) => `A stage is still called “${name}”.`,
    field: (name: string) => `New name for ${name}`,
    save: 'Save',
    saved: (name: string) => `Renamed to ${name}`,
    editStage: 'Rename',
  },

  loading: 'Loading the trip…',
  packError: 'Couldn’t load the trip. Connect once and reopen.',
}
